import { ErrorApi } from '../api/errores'
import { IconoActualizar, IconoAviso } from './iconos'

interface Props {
  error: unknown
  onReintentar?: () => void
}

const MENSAJE_DESCONOCIDO = 'No se ha podido completar la operación. Inténtalo de nuevo.'

type Consejo = 'reintentar' | 'configurar' | 'no_disponible' | 'ninguno'

/**
 * Un 503 del backend significa que un servicio externo no ha contestado, no que
 * el panel este roto. Merece un texto propio: "vuelve a intentarlo" es
 * accionable para el operador, "algo ha fallado" no lo es.
 *
 * El texto no nombra a Holded, aunque durante un tiempo fue el unico externo:
 * agendamiento habla con Odoo, y un 503 de Odoo explicado como un fallo de
 * Holded manda al operador a mirar donde no es. El mensaje concreto ya viene
 * del backend ("El ERP no responde", "Holded no responde") y es el que se pinta
 * arriba; esta segunda linea solo dice que hacer.
 *
 * Y hay un 503 que no se arregla esperando: el que dice que al servidor le
 * faltan las variables de entorno del externo. Ahi "vuelve a intentarlo en unos
 * segundos" es un consejo falso —el operador puede reintentar toda la tarde— y
 * el boton tampoco pinta nada, asi que ninguno de los dos aparece.
 */
function describir(error: unknown): { titulo: string; detalle: string; consejo: Consejo } {
  // La funcion no existe en el backend (ver src/api/enlace.ts). No es un fallo:
  // la pantalla lo dice y no ofrece reintentar.
  if (error instanceof ErrorApi && error.esNoDisponible) {
    return {
      titulo: 'Función no disponible',
      detalle: error.mensaje,
      consejo: 'no_disponible',
    }
  }
  if (error instanceof ErrorApi && error.esSinConfigurar) {
    return {
      titulo: 'Falta configurar la conexión',
      detalle: error.mensaje,
      consejo: 'configurar',
    }
  }
  if (error instanceof ErrorApi && error.estado === 503) {
    return {
      titulo: 'El servicio externo no responde',
      detalle: error.mensaje,
      consejo: 'reintentar',
    }
  }
  return {
    titulo: 'Algo ha fallado',
    detalle: error instanceof ErrorApi ? error.mensaje : MENSAJE_DESCONOCIDO,
    consejo: 'ninguno',
  }
}

export function EstadoError({ error, onReintentar }: Props) {
  const { titulo, detalle, consejo } = describir(error)
  const sinConfigurar = consejo === 'configurar'
  const noDisponible = consejo === 'no_disponible'

  return (
    <div
      role="alert"
      className="flex flex-col items-center justify-center gap-3 rounded-xl border border-aviso-borde bg-aviso-fondo px-6 py-10 text-center"
    >
      <IconoAviso aria-hidden="true" className="size-8 text-aviso-icono" />
      <p className="text-sm font-semibold text-aviso-titulo">{titulo}</p>
      <p className="max-w-sm text-sm text-aviso-tinta">{detalle}</p>
      {consejo === 'reintentar' ? (
        <p className="max-w-sm text-sm text-aviso-tinta">
          El sistema externo no está contestando ahora mismo. Los datos siguen ahí: vuelve a
          intentarlo en unos segundos.
        </p>
      ) : null}
      {sinConfigurar ? (
        <p className="max-w-sm text-sm text-aviso-tinta">
          Es un ajuste del servidor, no un fallo pasajero: reintentar no lo arregla. Hay que añadir
          esas variables al entorno del backend y volver a desplegar.
        </p>
      ) : null}
      {noDisponible ? (
        <p className="max-w-sm text-sm text-aviso-tinta">
          El backend de Trazabis Agendamiento todavía no ofrece esta función. El resto del panel
          sigue funcionando con normalidad.
        </p>
      ) : null}
      {onReintentar && !sinConfigurar && !noDisponible ? (
        <button
          type="button"
          onClick={onReintentar}
          className="mt-1 inline-flex items-center gap-2 rounded-lg border border-aviso-borde bg-aviso-boton px-3 py-2 text-sm font-medium text-aviso-titulo transition-colors hover:bg-aviso-boton-hover"
        >
          <IconoActualizar aria-hidden="true" className="size-4" />
          <span>Reintentar</span>
        </button>
      ) : null}
    </div>
  )
}
